import { useState } from 'react';
import { useController } from 'react-hook-form';
import { Field as AdminField } from '@/components/ui/admin/field';
import { Icon } from '@/components/ui/icon';
import type { RegisteredFieldProps } from '@/lib/component-registry';
import { registerFieldComponent } from '@/lib/component-registry';
import { mask } from '@/lib/mask';

/**
 * Campo composto de endereço via CEP (ViaCEP) — usado pela screen "soldiers" (`type: 'component',
 * component: 'address-cep'`). Os nomes dos campos são fixos (schema de `soldiers`): ao completar o
 * CEP, busca e preenche rua/bairro/cidade/estado automaticamente. Esses 4 ficam sempre desabilitados
 * (só o CEP os alimenta) — número e complemento continuam livres pra digitação manual.
 */
function AddressCepField({ control, disabled }: RegisteredFieldProps) {
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const cep = useController({ control, name: 'addressCep' as never });
  const street = useController({ control, name: 'addressStreet' as never });
  const number = useController({ control, name: 'addressNumber' as never });
  const complement = useController({ control, name: 'addressComplement' as never });
  const neighborhood = useController({ control, name: 'addressNeighborhood' as never });
  const city = useController({ control, name: 'addressCity' as never });
  const state = useController({ control, name: 'addressState' as never });

  async function lookupCep(rawValue: string) {
    const digits = rawValue.replace(/\D/g, '');
    if (digits.length !== 8) return;

    setLoading(true);
    setNotFound(false);

    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();

      if (data.erro) {
        setNotFound(true);
        return;
      }

      street.field.onChange(data.logradouro ?? '');
      neighborhood.field.onChange(data.bairro ?? '');
      city.field.onChange(data.localidade ?? '');
      state.field.onChange(data.uf ?? '');
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }

  const readOnlyFieldsDisabled = disabled || true;

  return (
    <>
      <div className="col-span-12 grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-4">
          <AdminField
            label="CEP"
            required
            disabled={disabled}
            value={cep.field.value ?? ''}
            onChange={(e) =>
              cep.field.onChange(
                mask({ value: e.target.value.replace(/\D/g, ''), mask: '99999-999' }),
              )
            }
            onBlur={(e) => {
              cep.field.onBlur();
              lookupCep(e.target.value);
            }}
            placeholder="00000-000"
            trailing={
              loading ? (
                <Icon name="loader" className="size-4 animate-spin text-muted-foreground" />
              ) : undefined
            }
          />
          {notFound && (
            <p className="mt-1.5 text-xs font-medium text-destructive">CEP não encontrado.</p>
          )}
        </div>

        <div className="col-span-12 md:col-span-4">
          <AdminField
            label="Número"
            disabled={disabled}
            value={number.field.value ?? ''}
            onChange={(e) => number.field.onChange(e.target.value)}
            onBlur={number.field.onBlur}
          />
        </div>

        <div className="col-span-12 md:col-span-4">
          <AdminField
            label="Complemento"
            disabled={disabled}
            value={complement.field.value ?? ''}
            onChange={(e) => complement.field.onChange(e.target.value)}
            onBlur={complement.field.onBlur}
          />
        </div>

        <div className="col-span-12 md:col-span-6">
          <AdminField
            label="Rua"
            disabled={readOnlyFieldsDisabled}
            value={street.field.value ?? ''}
            placeholder="Preenchido pelo CEP"
            readOnly
          />
        </div>

        <div className="col-span-12 md:col-span-6">
          <AdminField
            label="Bairro"
            disabled={readOnlyFieldsDisabled}
            value={neighborhood.field.value ?? ''}
            placeholder="Preenchido pelo CEP"
            readOnly
          />
        </div>

        <div className="col-span-12 md:col-span-6">
          <AdminField
            label="Cidade"
            disabled={readOnlyFieldsDisabled}
            value={city.field.value ?? ''}
            placeholder="Preenchido pelo CEP"
            readOnly
          />
        </div>

        <div className="col-span-12 md:col-span-6">
          <AdminField
            label="Estado"
            disabled={readOnlyFieldsDisabled}
            value={state.field.value ?? ''}
            placeholder="Preenchido pelo CEP"
            readOnly
          />
        </div>
      </div>
    </>
  );
}

registerFieldComponent('address-cep', AddressCepField);
